// react
import { useContext, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';
import { ContextContracts, ContextProjects } from '../../layouts/Providers/Providers.context';

// types
import { Contract } from '../../../@types';

// mui
import { Alert, Container, Grid, Skeleton, Typography } from '@mui/material';

// cards
import { CardContract } from '../../cards/CardContract/CardContract';

// dialogs
import { DialogAddInvoice } from '../../dialogs/DialogAddInvoice/DialogAddInvoice';

export const Contracts = () => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramsDialog = searchParams.get('dialog');

	// context
	const { contracts, isLoadingContracts } = useContext(ContextContracts);
	const { projects } = useContext(ContextProjects);
	const { setBanner } = useContext(ContextBanner);

	// state
	const [contractSelected, setContractSelected] = useState<Contract | null>(null);

	// handlers
	const handleSubmitInvoice = (contract: Contract) => {
		setContractSelected(contract);
		searchParams.set('dialog', 'addInvoice');
		searchParams.set('dialogContract', String(contract.id));
		setSearchParams(searchParams);
	};

	const handleCloseDialog = () => {
		setContractSelected(null);
		searchParams.delete('dialog');
		searchParams.delete('dialogContract');
		setSearchParams(searchParams);
	};

	const handleInvoiceSubmitSuccess = () => {
		handleCloseDialog();
		setBanner({
			color: 'success',
			hasClose: true,
			message: t('invoiceSubmitted', { defaultValue: 'Invoice submitted successfully.' }),
		});
	};

	// V2 contracts collect invoices per milestone on the contract detail page,
	// so the standalone "Submit Invoice" action is only offered for V1 contracts.
	const isContractV2 = (contract: Contract): boolean => {
		const projectId =
			typeof contract.project === 'number' ? contract.project : contract.project?.id;
		return projects.some((p) => p.id === projectId && p.version === '2');
	};

	const renderContent = () => {
		if (isLoadingContracts) {
			return (
				<Grid item xs={12}>
					<Grid container spacing={2}>
						{[...Array(3)].map((a, i) => {
							return (
								<Grid item key={`skeleton-${i}`} xs={12}>
									<Skeleton height={140} variant="rounded" />
								</Grid>
							);
						})}
					</Grid>
				</Grid>
			);
		}

		if (contracts.length) {
			return contracts.map((contract) => {
				return (
					<Grid item key={`contract-${contract.id}`} xs={12}>
						<CardContract
							contract={contract}
							onSubmitInvoice={isContractV2(contract) ? undefined : handleSubmitInvoice}
						/>
					</Grid>
				);
			});
		}

		return (
			<Grid item xs={12}>
				<Alert severity="info" variant="filled">
					{t('alert.empty')}
				</Alert>
			</Grid>
		);
	};

	return (
		<>
			<Container maxWidth={false}>
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<Typography color="common.white" variant="h2">
							{t('contracts')}
						</Typography>
					</Grid>
					{renderContent()}
				</Grid>
			</Container>
			<DialogAddInvoice
				contract={contractSelected}
				isOpen={paramsDialog === 'addInvoice'}
				onClose={handleCloseDialog}
				onSuccess={handleInvoiceSubmitSuccess}
			/>
		</>
	);
};
